import React, { type FunctionComponent, useMemo } from 'react'
import { Alert } from '@retailer-platform/shared-components'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
} from '../../shared-styled-components/SharedStyledComponents'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
} from '../../../types/attributeManagement.types.ts'

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

const MAX_NUM = 10
const MIN_NUM = 1

export const PipelineFormStepControlFieldMatch: FunctionComponent<Props> = ({ step, onChange }) => {
  const i18n = useDomainMessages({
    field:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.field',
    numberOfFields:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.numberOfFields',
    countsRequired:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.countsRequired',
    winningValueOutputField:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.winningValueOutputField',
    winningValueOutputFieldDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.winningValueOutputFieldDescription',
  })

  const nextStepsRule: PipelineStepNextStepsRule = useMemo(
    () => ({
      type: AttributeManagementNextStepTypeEnum.FieldMatch,
      countRequired: step?.nextStepsRule?.countRequired ?? 2,
      campFields: step?.nextStepsRule?.campFields ?? ['', ''],
      nextPipelineStepOnMatchHit: step?.nextStepsRule?.nextPipelineStepOnMatchHit ?? '',
      nextPipelineStepOnMatchMiss: step?.nextStepsRule?.nextPipelineStepOnMatchMiss ?? '',
      possibleNextSteps: step?.nextStepsRule?.possibleNextSteps ?? [],
      winningValueOutputField: step?.nextStepsRule?.winningValueOutputField ?? '',
    }),
    [
      step?.nextStepsRule?.campFields,
      step?.nextStepsRule?.countRequired,
      step?.nextStepsRule?.nextPipelineStepOnMatchHit,
      step?.nextStepsRule?.nextPipelineStepOnMatchMiss,
      step?.nextStepsRule?.possibleNextSteps,
      step?.nextStepsRule?.winningValueOutputField,
    ]
  )

  return (
    <>
      <PropertyContainer style={{ flexDirection: 'column' }}>
        <PropertyRow>
          <PropertyTitle>{i18n.numberOfFields}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.campFields?.length ?? 2}
            onChange={e => {
              const amount = Math.min(MAX_NUM, Math.max(MIN_NUM, Number.parseInt(e.target.value)))

              const fields: string[] = []
              for (let i = 0; i < amount; i++) {
                fields.push(nextStepsRule?.campFields?.[i] ?? '')
              }

              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  campFields: fields,
                },
              })
            }}
          />
        </PropertyRow>
        {(nextStepsRule.campFields ?? ['', '']).map((field, index) => {
          const trimmedField = field?.trim()
          const isDuplicate = Boolean(
            trimmedField &&
              nextStepsRule.campFields?.some(
                (otherField, otherIndex) =>
                  otherIndex !== index && otherField?.trim() === trimmedField
              )
          )

          return (
            <PropertyRow key={`${index}`}>
              <PropertyTitle>
                {i18n.field} {index + 1}:
              </PropertyTitle>
              <Input
                type={'text'}
                value={field}
                error={!trimmedField || isDuplicate}
                onChange={e => {
                  if (!nextStepsRule.campFields) return
                  nextStepsRule.campFields = [...nextStepsRule.campFields]
                  nextStepsRule.campFields[index] = e.target.value
                  onChange({
                    ...step,
                    nextStepsRule: nextStepsRule,
                  })
                }}
              />
            </PropertyRow>
          )
        })}

        <PropertyRow>
          <PropertyTitle>{i18n.countsRequired}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.countRequired}
            onChange={e => {
              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  countRequired: Math.max(
                    0,
                    Math.min(
                      (nextStepsRule.campFields ?? []).length,
                      Number.parseInt(e.target.value)
                    )
                  ),
                },
              })
            }}
          />
        </PropertyRow>
        <PropertyRow css={{ maxWidth: '300px', minWidth: '100%' }}>
          <Alert variant={'info'}>{i18n.winningValueOutputFieldDescription}</Alert>
        </PropertyRow>
        <PropertyRow>
          <PropertyTitle>{i18n.winningValueOutputField}</PropertyTitle>
          <Input
            type={'text'}
            value={nextStepsRule.winningValueOutputField}
            error={!nextStepsRule.winningValueOutputField?.trim()}
            onChange={e => {
              onChange({
                ...step,
                nextStepsRule: { ...nextStepsRule, winningValueOutputField: e.target.value },
              })
            }}
          />
        </PropertyRow>
      </PropertyContainer>
    </>
  )
}
